import * as React from 'react';
import { isEqual } from 'lodash-es';
import { match, useRouteMatch } from 'react-router';
import * as qs from 'query-string';
import { Search, Preferences, Venues, SearchApi, PreferencesApi } from '@weddingspot/ws-api-client';
import { WSAppContext } from '@weddingspot/ws-core-components';

import { createHookFromAPI, DataFetchStatus } from './ApiHookCreator';
import { getVariantFromMatch, URLToSearchApiParams, URLToFilterApiParams, getCanonicalUrl, parsePrimaryLocation } from '../utils';
import { SRPRouteMatchProps } from '..';
import { asArray } from '../../../utils/arrays';
import { firstOrItem } from '../../../utils/query';

/**
 * Memoizes the value if deep comparison check fails to find it equal to the previous value
 *
 * @param value value to memoize
 */
export const useDeepCompareMemoize = <T extends unknown>(value: T) => {
    const ref = React.useRef<T>(value);

    if (!isEqual(value, ref.current)) {
        ref.current = value;
    }

    return ref.current;
};

/**
 * Given a list of dependencies that will be passed to an effect, return a value
 * that is only updated if all the dependencies have changed. An empty array is
 * an invalid value for this function and cause an infinite loop
 *
 * @param deps React effect dependencies array
 */
export const useLogicalAndDependencies = (deps: any[]) => {
    const prevDeps = React.useRef(deps);

    if (deps.length !== prevDeps.current.length) {
        prevDeps.current = deps;
        return prevDeps.current;
    }

    const allChanged = deps
        .map((d, idx) => {
            const prevDep = prevDeps.current[idx];
            return prevDep !== d;
        })
        .reduce((prev, cur) => {
            return prev && cur;
        }, true);

    if (allChanged) {
        prevDeps.current = deps;
    }

    return prevDeps.current;
};

/**
 * Hook for fetching venues for the SRP page based on the URL match and params
 */
export const useVenues = createHookFromAPI<Search.SearchResults, SRPRouteMatchProps>((match, search) => {
    return SearchApi.getVenues(URLToSearchApiParams(match, search as Record<string, string>));
}, 'searchService.getSearchResults');

/**
 * Hook for fetching filter choices from API
 */
export const useFilters = createHookFromAPI((match, search) => {
    const filterApiParams = URLToFilterApiParams(match, search as Record<string, string>);
    return SearchApi.getFilters(filterApiParams);
}, 'useFilterPossibleValues');

/**
 * Hook for fetching initial page metadata based on the URL match and params
 */
export const usePageMetadata = createHookFromAPI<Search.SearchMetadata, SRPRouteMatchProps>((match: match<SRPRouteMatchProps>, search) => {
    const { market: marketToken, submarket: subMarketToken, region: regionToken, zipcode: zipcodeToken, style: styleToken } = match.params;

    const srpVariant = getVariantFromMatch(match);
    // Resolve zipcode
    let zipcode = zipcodeToken;
    if (search.z) {
        zipcode = asArray(search.z)[0];
    }

    // Convert URL tokens into params for the metadata endpoint
    let params: Search.SearchMetadataQueryParams = {
        zip_code: zipcode,
        is_rd: srpVariant === 'rd',
        is_affordable: srpVariant === 'affordable',
        ...(srpVariant === 'affordable' ? {} : { style_name: styleToken }),
    };

    // First look @ the URL parameters for the primary location...
    if (search.pr) {
        params.location = firstOrItem(search.pr);
    } else if (marketToken || regionToken) {
        // Try to parse the location from the URL path
        if (marketToken && regionToken) {
            params.location = `${marketToken}:${regionToken}`;
        } else if (marketToken && subMarketToken) {
            params.location = `${marketToken}:${subMarketToken}`;
        } else if (marketToken) {
            params.location = marketToken;
        }
    }

    return new Promise((resolve, reject) => {
        SearchApi.getMetadata(params)
            .then(resolve)
            .catch((res) => {
                let error: DataFetchStatus = 'error';
                let details: string | undefined;
                if (res.status === 400) {
                    const locationError = res.detail?.content?.locations;
                    const styleError = res.detail?.content?.style_name;
                    if (locationError || styleError) {
                        error = 'not_found';
                    }
                } else if (res.status === 301) {
                    error = 'permanent_redirect';
                    const _style = res.detail?.content?.detail;
                    const page = firstOrItem(search.page);
                    details = getCanonicalUrl({
                        variant: srpVariant,
                        styleUrlName: _style,
                        market: match.params.market,
                        region: match.params.region,
                        zipcode: match.params.zipcode,
                        page: page ? parseInt(page, 10) : undefined,
                    });
                }

                reject({
                    apiHookStatus: error,
                    details: details,
                });
            });
    });
}, 'searchService.getPageMetadata');

/**
 * Hook for fetching personalized content for SRP
 * @param venues the state variable for the search result, should be used in conjunction with a venue data fetching hook
 * @returns stateful variable for the personalized content
 */
export const usePersonalizedData = (venues: Search.SearchResults | undefined | null) => {
    const { user } = React.useContext(WSAppContext);
    const [personalized, setPersonalized] = React.useState<Search.SearchPersonalizedData>();

    React.useEffect(() => {
        if (!venues || !user) {
            return;
        }
        console.log('fetching personalized');

        const venueIds = [...venues.venues, ...venues.backfill_venues].map((v) => v.id);
        SearchApi.getPersonalizedData({ venue_ids: venueIds }).then((res) => setPersonalized(res));
    }, [venues, user]);
    return personalized;
};

/**
 * Hook for getting the current SRP variant
 * @returns {'affordable' | 'rd' | undefined | null} current SRP variant, undefined is
 *  the base wedding venues SRP variant and null occurrs if there is no implicit match
 */
export const useSrpVariant = () => {
    const match = useRouteMatch();
    return match ? getVariantFromMatch(match) : null;
};

/**
 * Hook for fetching frontend preferences
 * @returns stateful variable for the personalized content
 */
export const useFrontendPreferences = () => {
    const { userIsAuthenticated } = React.useContext(WSAppContext);
    const [preferences, setPreferences] = React.useState<Preferences.Preference[]>();

    React.useEffect(() => {
        if (!userIsAuthenticated) {
            return;
        }

        PreferencesApi.loadPreferences().then((res) => setPreferences(res));
    }, [userIsAuthenticated]);
    return preferences;
};

/**
 * Hook for logging directory impressions every time the set of displayed venues changes
 */
export const useDirectoryImpressionsEffect = (
    results: Search.SearchResults | null | undefined,
    match: match<SRPRouteMatchProps>,
    search: qs.ParsedQuery<string>
) => {
    React.useEffect(() => {
        if (!results) {
            return;
        }

        const venueIds = [...results.venues, ...results.backfill_venues].map((v) => v.id);
        if (venueIds.length == 0) {
            return;
        }

        let location = parsePrimaryLocation(match, search);
        if (!location) {
            location = 'All Venues';
        }

        let page = 1;
        try {
            const pageParam = firstOrItem(search.page);
            if (pageParam !== undefined) {
                page = parseInt(pageParam, 10);
            }
        } catch {}

        const variantStr = getVariantFromMatch(match);
        let variant = Venues.VenueMetricsMetaType.WEDDING_METRIC;
        if (variantStr == 'rd') {
            variant = Venues.VenueMetricsMetaType.RD_METRIC;
        }

        SearchApi.logDirectoryImpressions({
            venue_ids: venueIds,
            location: location,
            page_number: page,
            metrics_metatype: variant,
        }).catch();
    }, [results]);
};
